import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Activity, Compass, Share2 } from 'lucide-react';
import { AnimatedBackground } from '../components/AnimatedBackground';
import { TreeRingsHero } from '../components/TreeRingsHero';
const heartbeatPath = 'M 0,200 L 80,200 L 100,200 L 120,180 L 140,220 L 155,120 L 170,280 L 185,160 L 200,200 L 260,200 L 300,200 L 320,180 L 340,220 L 355,100 L 370,300 L 385,140 L 400,200 L 460,200 L 500,200 L 520,180 L 540,220 L 555,110 L 570,290 L 585,150 L 600,200 L 660,200 L 700,200 L 720,180 L 740,220 L 755,105 L 770,295 L 785,145 L 800,200 L 860,200 L 900,200';
const valueLabels = [{
  name: 'CONVICTION',
  x: 170,
  color: '#78e1c4',
  delay: 0.8
}, {
  name: 'ATHLETICISM',
  x: 370,
  color: '#4DE7D4',
  delay: 1.6
}, {
  name: 'ADVENTURE',
  x: 570,
  color: '#a0e6e9',
  delay: 2.4
}, {
  name: 'CONNECTION',
  x: 770,
  color: '#edcd99',
  delay: 3.2
}];
export function Values() {
  const values = [{
    title: 'CONVICTION',
    desc: 'Fuel startups that are creating solutions inspired by and respecting the intelligence of nature',
    icon: Heart,
    color: '#78e1c4',
    position: 'top-0 left-0 md:left-[5%]',
    size: 'w-full md:w-[45%]',
    clipPath: 'polygon(20% 0%, 80% 0%, 100% 30%, 95% 70%, 75% 100%, 25% 100%, 5% 70%, 0% 30%)'
  }, {
    title: 'ATHLETICISM',
    desc: 'Approach big challenges like training—balancing mindset, grit, discipline, and recovery to navigate the unknowns and support peak performance',
    icon: Activity,
    color: '#4DE7D4',
    position: 'top-0 right-0 md:right-[5%]',
    size: 'w-full md:w-[45%]',
    clipPath: 'polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 70% 100%, 20% 100%, 0% 75%, 5% 25%)'
  }, {
    title: 'ADVENTURE',
    desc: 'Engage new ideas and bold ventures with intellectual curiosity and a spirit of exploration',
    icon: Compass,
    color: '#a0e6e9',
    position: 'bottom-0 left-0 md:left-[5%]',
    size: 'w-full md:w-[45%]',
    clipPath: 'polygon(25% 0%, 75% 0%, 95% 30%, 100% 70%, 80% 100%, 20% 100%, 0% 70%, 5% 30%)'
  }, {
    title: 'CONNECTION',
    desc: 'Work across disciplines, sectors, and industries to create partnerships, insights, and breakthroughs',
    icon: Share2,
    color: '#edcd99',
    position: 'bottom-0 right-0 md:right-[5%]',
    size: 'w-full md:w-[45%]',
    clipPath: 'polygon(20% 0%, 80% 0%, 100% 25%, 95% 75%, 75% 100%, 25% 100%, 0% 75%, 5% 25%)'
  }];
  return <div className="min-h-screen pt-20" data-id="element-1773">
      {/* Hero Section — Heartbeat / Vital Signs */}
      <section className="relative py-24 overflow-hidden min-h-[60vh] flex items-center" data-id="element-1774">
        <div className="absolute inset-0 bg-gradient-to-b from-[#070f14] via-[#0e2533] to-[#0e2533] z-0" data-id="element-1775" />

        {/* Subtle grid texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(120,225,196,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(120,225,196,0.3) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-1776" />

        {/* Heartbeat SVG — positioned in upper portion, more transparent */}
        <div className="absolute top-0 left-0 right-0 h-[45%] flex items-center z-[1] opacity-30" data-id="element-1777">
          <svg viewBox="0 0 900 400" className="w-full h-full" preserveAspectRatio="xMidYMid meet" style={{
          filter: 'drop-shadow(0 0 8px rgba(77, 231, 212, 0.15))'
        }} data-id="element-1778">
            <defs data-id="element-1779">
              <linearGradient id="pulseGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-1780">
                <stop offset="0%" stopColor="#78e1c4" stopOpacity="0.05" data-id="element-1781" />
                <stop offset="30%" stopColor="#4DE7D4" stopOpacity="0.6" data-id="element-1782" />
                <stop offset="70%" stopColor="#4DE7D4" stopOpacity="0.6" data-id="element-1783" />
                <stop offset="100%" stopColor="#edcd99" stopOpacity="0.05" data-id="element-1784" />
              </linearGradient>
              <filter id="pulseGlow" data-id="element-1785">
                <feGaussianBlur stdDeviation="6" result="blur" data-id="element-1786" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-1787" />
              </filter>
            </defs>

            {/* Background flatline */}
            <line x1="0" y1="200" x2="900" y2="200" stroke="#78e1c4" strokeWidth="1" strokeOpacity="0.06" data-id="element-1788" />

            {/* Glow layer */}
            <motion.path d={heartbeatPath} fill="none" stroke="url(#pulseGradient)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" filter="url(#pulseGlow)" initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 0.3
          }} transition={{
            duration: 4,
            ease: 'easeInOut',
            delay: 0.3
          }} data-id="element-1789" />

            {/* Main heartbeat line */}
            <motion.path d={heartbeatPath} fill="none" stroke="#4DE7D4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 0.7
          }} transition={{
            duration: 4,
            ease: 'easeInOut',
            delay: 0.3
          }} data-id="element-1790" />

            {/* Traveling pulse dot */}
            <motion.circle r="4" fill="#4DE7D4" filter="url(#pulseGlow)" initial={{
            opacity: 0
          }} animate={{
            opacity: [0, 0.6, 0.6, 0]
          }} transition={{
            delay: 5,
            duration: 3,
            repeat: Infinity,
            repeatDelay: 2
          }} data-id="element-1791">
              <animateMotion dur="3s" repeatCount="indefinite" begin="5s" data-id="element-1792">
                <mpath href="#heartbeatMotionPath" data-id="element-1793" />
              </animateMotion>
            </motion.circle>
            <path id="heartbeatMotionPath" d={heartbeatPath} fill="none" opacity="0" data-id="element-1794" />
          </svg>
        </div>

        {/* Content overlay */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center w-full" data-id="element-1795">
          <motion.h1 initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6
        }} className="text-6xl md:text-8xl font-bold text-white tracking-tight" data-id="element-1796">
            VALUES
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-vitalityTeal mt-4" data-id="element-1797">
            The living principles that guide our work
          </motion.p>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0e2533] to-transparent z-[2]" data-id="element-1798" />
      </section>

      {/* Cellular Grid / Organic Tessellation */}
      <section className="py-32 bg-[#0e2533] relative overflow-hidden" data-id="element-1799">
        {/* Background cellular pattern */}
        <div className="absolute inset-0 opacity-5" data-id="element-1800">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-1801">
            <defs data-id="element-1802">
              <pattern id="cellPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse" data-id="element-1803">
                <circle cx="50" cy="50" r="30" fill="none" stroke="#78e1c4" strokeWidth="1" opacity="0.3" data-id="element-1804" />
                <circle cx="50" cy="50" r="20" fill="none" stroke="#4DE7D4" strokeWidth="1" opacity="0.2" data-id="element-1805" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cellPattern)" data-id="element-1806" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1807">
          {/* Organic cellular grid */}
          <div className="relative flex flex-col gap-8 md:gap-0 md:min-h-[900px]" data-id="element-1808">
            {values.map((value, index) => <motion.div key={value.title} initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: index * 0.15
          }} className={`relative md:absolute ${value.position} ${value.size} p-4`} style={{
            top: index < 2 ? typeof window !== 'undefined' && window.innerWidth >= 768 ? '0' : 'auto' : 'auto',
            bottom: index >= 2 ? typeof window !== 'undefined' && window.innerWidth >= 768 ? '0' : 'auto' : 'auto'
          }} data-id="element-1809">
                {/* Cellular glow */}
                <motion.div className="absolute -inset-4 blur-2xl opacity-30" style={{
              backgroundColor: value.color,
              clipPath: value.clipPath
            }} animate={{
              opacity: [0.2, 0.4, 0.2],
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 4 + index,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-1810" />

                {/* Cell membrane (outer border) */}
                <div className="relative bg-[#1c3f4d]/40 backdrop-blur-sm border-2 overflow-hidden" style={{
              clipPath: value.clipPath,
              borderColor: `${value.color}60`,
              minHeight: '400px'
            }} data-id="element-1811">
                  {/* Cellular texture overlay */}
                  <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `radial-gradient(circle at 30% 40%, ${value.color} 1px, transparent 1px),
                                       radial-gradient(circle at 70% 60%, ${value.color} 1px, transparent 1px)`,
                backgroundSize: '30px 30px, 40px 40px'
              }} data-id="element-1812" />

                  {/* Breathing animation overlay */}
                  <motion.div className="absolute inset-0" style={{
                backgroundColor: `${value.color}10`
              }} animate={{
                opacity: [0.1, 0.3, 0.1]
              }} transition={{
                duration: 3 + index * 0.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1813" />

                  {/* Content */}
                  <div className="relative z-10 p-8 md:p-10 flex flex-col items-center text-center h-full justify-center" data-id="element-1814">
                    {/* Nucleus (icon container) */}
                    <motion.div className="relative mb-6" animate={{
                  rotate: [0, 5, -5, 0]
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-1815">
                      <div className="w-20 h-20 flex items-center justify-center relative" style={{
                    clipPath: 'circle(50% at 50% 50%)',
                    backgroundColor: `${value.color}30`,
                    border: `2px solid ${value.color}60`
                  }} data-id="element-1816">
                        <value.icon className="w-9 h-9" style={{
                      color: value.color
                    }} data-id="element-1817" />
                      </div>

                      {/* Pulsing membrane */}
                      <motion.div className="absolute inset-0 rounded-full" style={{
                    border: `2px solid ${value.color}`
                  }} animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.6, 0, 0.6]
                  }} transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: index * 0.3
                  }} data-id="element-1818" />
                    </motion.div>

                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-wide" data-id="element-1819">
                      {value.title}
                    </h3>

                    <p className="text-gray-300 leading-relaxed text-base md:text-lg max-w-md" data-id="element-1820">
                      {value.desc}
                    </p>

                    {/* Cell activity indicator */}
                    <div className="mt-6 flex items-center space-x-2 text-xs" style={{
                  color: value.color
                }} data-id="element-1821"></div>
                  </div>
                </div>

                {/* Cell connections (visible on desktop) */}
                {index < 2 && <motion.div className="hidden md:block absolute top-1/2 -right-8 w-16 h-0.5 z-0" style={{
              backgroundColor: `${value.color}40`
            }} initial={{
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 1,
              delay: 0.5 + index * 0.2
            }} data-id="element-1822" />}
              </motion.div>)}
          </div>
        </div>
      </section>
    </div>;
}